import { ATTRIBUTES, BELIMO, INTEGRATED_SWITCH_ATTRS, NO, NULL_ARRAY_STRING, YES } from '../constants';
import { createCaseWhenSql } from './createCaseWhenSql';
import { FAIL_SAFE_MAPPING } from '../resolvers/MapFilterValues.pipe';
import { extractJsonFieldSql } from './extractJsonFieldSql';
import { getAltId } from '../resolvers/RetrofitAttributesMapping';
import { LocalesEnum } from '../enums/LocalesEnum';

export function prepareValuesMapping(
    id: string,
    rawName: LocalesEnum,
    skipAttr: string[] = [],
) {
    // @TODO move id sanitization to validation pipe
    const safeId = parseInt(id, 10).toString();

    const defaultLocale = rawName;
    const formatLocalizedValue = (value: string): string => JSON.stringify({ [defaultLocale]: [value] });
    let localized;
    let attrValue;
    const exclude = [safeId];
    if (safeId === ATTRIBUTES.RETROFIT_FAIL_SAFE) {
        exclude.push(ATTRIBUTES.FAIL_SAFE);
        localized = createCaseWhenSql(
            FAIL_SAFE_MAPPING,
            extractJsonFieldSql(
                `attributes`,
                `$.${ATTRIBUTES.FAIL_SAFE}.${defaultLocale}`,
                extractJsonFieldSql(
                    `attributes`,
                    `$.${safeId}.${defaultLocale}`,
                    `'${NULL_ARRAY_STRING}'`,
                ),
            ),
            (condition) => JSON.stringify([condition]),
            JSON.stringify({ [defaultLocale]: [YES] }),
            JSON.stringify({ [defaultLocale]: [NO] }),
        );
        attrValue = createCaseWhenSql(
            FAIL_SAFE_MAPPING,
            extractJsonFieldSql(
                `attributes`,
                `$.${ATTRIBUTES.FAIL_SAFE}.${defaultLocale}`,
                extractJsonFieldSql(
                    `attributes`,
                    `$.${safeId}.${defaultLocale}`,
                    `'${NULL_ARRAY_STRING}'`,
                ),
            ),
            (condition) => JSON.stringify([condition]),
            JSON.stringify([YES]),
            JSON.stringify([NO]),
        );
    } else if (safeId === ATTRIBUTES.FAIL_SAFE) {
        localized = createCaseWhenSql(
            FAIL_SAFE_MAPPING,
            extractJsonFieldSql(
                `attributes`,
                `$.${safeId}.${defaultLocale}`,
                `'${NULL_ARRAY_STRING}'`,
            ),
            (condition) => JSON.stringify([condition]),
            JSON.stringify({ [defaultLocale]: [YES] }),
            JSON.stringify({ [defaultLocale]: [NO] }),
        );
        attrValue = createCaseWhenSql(
            FAIL_SAFE_MAPPING,
            extractJsonFieldSql(
                `attributes`,
                `$.${safeId}.${defaultLocale}`,
                `'${NULL_ARRAY_STRING}'`,
            ),
            (condition) => JSON.stringify([condition]),
            JSON.stringify([YES]),
            JSON.stringify([NO]),
        );
    } else if (safeId === ATTRIBUTES.COMPANY_COMPETITOR) {
        localized = extractJsonFieldSql(
            `attributes`,
            `$.${safeId}`,
            `'${formatLocalizedValue(BELIMO)}'`,
        );
        attrValue = extractJsonFieldSql(
            `attributes`,
            `$.${safeId}.${defaultLocale}`,
            `'${JSON.stringify(BELIMO)}'`,
        );
    } else if (safeId === ATTRIBUTES.PRESSURE_DEPENDENT) {
        const emptyLocalizedObject = JSON.stringify({ [defaultLocale]: [] });
        localized = `json_set('${emptyLocalizedObject}', '$.${defaultLocale}[0]', attrs.value)`;
        const extractedField = extractJsonFieldSql(
            `attributes`,
            `$.${safeId}.${defaultLocale}`,
            `'${NULL_ARRAY_STRING}'`
        );
        attrValue = `
            case lower(${extractedField})
                when 'no' then '["@no"]'
                when '["null"]' then '["@no"]'
                when '["no"]' then '["@no"]'
                else '["@yes"]'
                end
            `;
    } else if (safeId === ATTRIBUTES.DISPLAY_TYPE) {
        const emptyLocalizedObject = JSON.stringify({ [defaultLocale]: [] });
        localized = `json_set('${emptyLocalizedObject}', '$.${defaultLocale}[0]', attrs.value)`;
        const extractedField = extractJsonFieldSql(
            `attributes`,
            `$.${safeId}.${defaultLocale}`,
            `'${NULL_ARRAY_STRING}'`
        );
        attrValue = `
            case lower(${extractedField})
                when 'no' then '["@no"]'
                when '["null"]' then '["@no"]'
                else ${extractedField}
                end
            `;
    } else if (safeId === ATTRIBUTES.DISPLAY_TYPE_SAS) {
        const emptyLocalizedObject = JSON.stringify({ [defaultLocale]: [] });
        localized = `json_set('${emptyLocalizedObject}', '$.${defaultLocale}[0]', attrs.value)`;
        const extractedField = extractJsonFieldSql(
            `attributes`,
            `$.${safeId}.${defaultLocale}`,
            `'${NULL_ARRAY_STRING}'`
        );
        attrValue = `
            case lower(${extractedField})
                when 'no' then '["@no"]'
                when '["no"]' then '["@no"]'
                when '["null"]' then '["@no"]'
                else '["@yes"]'
                end
            `;
    } else if (INTEGRATED_SWITCH_ATTRS.includes(safeId)) {
        const emptyLocalizedObject = JSON.stringify({ [defaultLocale]: [] });
        localized = `json_set('${emptyLocalizedObject}', '$.${defaultLocale}[0]', attrs.value)`;
        if (safeId === ATTRIBUTES.INTEGRATED_SWITCH_COMPETITOR) {
            exclude.push(ATTRIBUTES.INTEGRATED_SWITCH);
        }
        const ifNullValue = safeId === ATTRIBUTES.INTEGRATED_SWITCH_COMPETITOR
            ? extractJsonFieldSql(
                `attributes`,
                `$.${ATTRIBUTES.INTEGRATED_SWITCH}.${defaultLocale}`,
                `'${NULL_ARRAY_STRING}'`,
            )
            : `'${NULL_ARRAY_STRING}'`;

        const extractedField = extractJsonFieldSql(
            `attributes`,
            `$.${safeId}.${defaultLocale}`,
            ifNullValue,
        );
        attrValue = `
            case ${extractedField}
                when '["NULL"]' then '["@no"]'
                when '["No"]' then '["@no"]'
                else ${extractedField}
                end
            `;
    } else if (safeId === ATTRIBUTES.MEDIA && !skipAttr.includes(safeId)) {
        // dropping `Water glycol mixture` from values because of customer will
        const emptyLocalizedObject = JSON.stringify({ [defaultLocale]: [] });
        localized = `json_set('${emptyLocalizedObject}', '$.${defaultLocale}[0]', attrs.value)`;
        const extractedField = extractJsonFieldSql(
            `attributes`,
            `$.${safeId}.${defaultLocale}`,
            `'${NULL_ARRAY_STRING}'`,
        );
        attrValue = `
            case true
                when UPPER(${extractedField}) like '%WATER%' and UPPER(${extractedField}) like '%AIR%' then '["air","water"]'
                when UPPER(${extractedField}) like '%WATER%' then '["water"]'
                when UPPER(${extractedField}) like '%AIR%' then '["air"]'
                else ${extractedField}
                end
            `;
    } else {
        const altId = getAltId(id);
        if (altId) {
            exclude.push(altId);
        }
        const localizedIfnull = altId === null
            ? undefined
            : extractJsonFieldSql(
                `attributes`,
                `$.${altId}`,
                undefined,
            );
        localized = extractJsonFieldSql(
            `attributes`,
            `$.${safeId}`,
            localizedIfnull,
        );
        const attrValueIfNull = altId === null
            ? undefined
            : extractJsonFieldSql(
                `attributes`,
                `$.${altId}.${defaultLocale}`,
                undefined,
            );
        attrValue = extractJsonFieldSql(
            `attributes`,
            `$.${safeId}.${defaultLocale}`,
            attrValueIfNull,
        );
    }
    return {
        attrValue,
        localized,
        exclude,
    }
}
